import ListViewHeaderTitle from 'App/components/ListView/ListViewHeader/ListViewHeaderTitle';
import DocumentAttributes from 'App/babel/attributes/DocumentAttributes';
import openEditEsign from 'App/components/Esign/EditEsign/EditEsign';
import EsignAttributes from 'App/babel/attributes/EsignAttributes';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import { Esign } from '../../../resources/Model/Esign';
import RequestBuilder from 'Resources/RequestBuilder';
import Document from 'App/resources/Model/Document';
import SimpleListView from '../../ClientCard/SimpleListView';
import openModal from 'App/services/Modal';
import React, { useEffect, useState } from 'react';
import { fetchSubAccountIds } from 'App/helpers/accountsHelper';
import { CardEntity } from './types';
import { Feature, isAvailable } from 'App/services/FeatureHelperStandalone';
import { PrimaryButton } from '@upsales/components/Buttons';
import { Icon } from '@upsales/components';

import '../../ClientCard/ContentPages/Documents/Documents.scss';
import AppService from 'App/legacy/AppService';
import EventBus from 'App/services/EventBus';

const COLUMNS_ESIGN = ['mdate', 'filename', 'opportunity', 'status', 'signingParties', 'delete'];
const COLUMNS_DOCUMENTS = ['mimetype', 'filename', 'size', 'uploadDate', 'user', 'fileRelation', 'delete'];

const getEsign = async (rb: RequestBuilder, ids: number[], isContact: boolean) => {
	rb.addFilter({ field: isContact ? 'involved.contactId' : 'client.id' }, 'eq', ids);
	return Tools.Esign.find(rb.build());
};

const getDocuments = async (rb: RequestBuilder, ids: number[], isContact: boolean) => {
	rb.addFilter({ field: isContact ? 'contact.id' : 'client.id' }, 'eq', ids);
	rb.addFilter({ field: 'mimetype' }, 'ne', 'text/calendar');
	rb.addFilter({ field: 'isInline' }, 'ne', true);
	return Tools.File.find(rb.build());
};

const onDocumentClick =
	({ id }: Document) =>
	() => {
		Tools.File.download(id);
	};

const isDraft = (item: Esign) => item.state === 0;

const onEsignClick = (esign: Esign) => () => {
	const options = {
		esign: {
			client: {
				id: esign.client.id
			}
		},
		id: esign?.id
	};

	// Only open edit if saved as draft
	if (!esign || isDraft(esign)) {
		openEditEsign(options);
	} else {
		openModal('ConfirmEsignModal', {
			id: options.id
		});
	}
};

const onEsignDelete = (item: Esign) => {
	return Tools.Esign.delete(item);
};

const onDocumentDelete = (item: Document) => {
	return Tools.File.delete({ id: item.id });
};

const uploadFile = (cardEntity: CardEntity) => {
	const params = cardEntity.type === 'contact' ? { contactId: cardEntity.id } : { accountId: cardEntity.id };
	Tools.$upModal.open('uploadFile', params);
};

const SharedDocuments = ({ cardEntity }: { cardEntity: CardEntity }) => {
	const { t } = useTranslation();
	const classes = new BemClass('ClientCardDocuments');
	const isContact = cardEntity.type === 'contact';
	const [entityIds, setEntityIds] = useState<number[]>([cardEntity.id]);
	const [filesListKey, setFilesListKey] = useState(0);

	useEffect(() => {
		if (!isContact) {
			// Only fetch subaccounts for clients
			// eslint-disable-next-line promise/catch-or-return
			fetchSubAccountIds(cardEntity.id).then(ids => {
				setEntityIds([cardEntity.id, ...ids]);
			});
		}
	}, [cardEntity.id]);

	useEffect(() => {
		// Uploads broadcast 'file.uploaded', which ListView's broadcastType (.added/.updated/.deleted) never matches
		const listener = EventBus.on('file.uploaded', () => setFilesListKey(key => key + 1));
		return () => listener();
	}, []);

	const hasEsign = isAvailable(Feature.ESIGN) && AppService.getEsignIntegrations().length;

	const hasDocuments = isAvailable(Feature.DOCUMENTS) || isAvailable(Feature.CLIENT_DOCUMENTS);

	return (
		<div className={classes.b()}>
			{hasEsign ? (
				<SimpleListView<Esign>
					classes={classes.elem('esign')}
					getData={(rb: RequestBuilder) => getEsign(rb, entityIds, isContact)}
					columns={COLUMNS_ESIGN}
					onRowClick={onEsignClick}
					attributes={EsignAttributes}
					useHeader
					renderHeaderFirstExtra={({ total }) => (
						<ListViewHeaderTitle title={t('default.eSign')} total={total} />
					)}
					broadcastType={['esign']}
					initialSorting={[{ attribute: 'mdate', ascending: false }]}
					tableLimitOptions={[25]}
					hideNoResults={true}
					deleteFn={onEsignDelete}
					isDeletable={item => item.userRemovable && isDraft(item)}
					entityName={'default.eSign'}
					noStickyHeader
				/>
			) : null}
			{hasDocuments ? (
				<SimpleListView<Document>
					key={filesListKey}
					classes={classes.elem('files')}
					getData={(rb: RequestBuilder) => getDocuments(rb, entityIds, isContact)}
					columns={COLUMNS_DOCUMENTS}
					onRowClick={onDocumentClick}
					attributes={DocumentAttributes}
					renderHeaderFirstExtra={({ total }) => (
						<ListViewHeaderTitle title={t('file.files')} total={total} />
					)}
					renderHeaderLastExtra={() => (
						<PrimaryButton onClick={() => uploadFile(cardEntity)}>
							<Icon name="plus" space="mrs" />
							{t('default.uploadFile')}
						</PrimaryButton>
					)}
					useHeader
					broadcastType={['file']}
					initialSorting={[{ attribute: 'uploadDate', ascending: false }]}
					tableLimitOptions={[25]}
					hideNoResults={true}
					deleteFn={onDocumentDelete}
					isDeletable={item => item.userRemovable}
					entityName={'file.file'}
					noStickyHeader
				/>
			) : null}
		</div>
	);
};

export default SharedDocuments;
